import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components/macro';
import { withRouter } from 'react-router-dom';

import FormCheckbox from 'components/FormCheckbox/FormCheckbox';
import FormInput from 'components/FormInput/FormInput';
import FormSelection from 'components/FormSelection/FormSelection';
import FormPhoneNumber from 'components/FormPhoneNumber/FormPhoneNumber';
import Button from 'components/Button/Button';
import Heading from 'components/Heading/Heading';
import GoogleMapsSearch from 'components/GoogleMapsSearch/GoogleMapsSearch';
import EnquirySubmitted from './components/EnquirySubmitted/EnquirySubmitted';

import { submitEnquiry } from 'utils/apis/enquiry';
import { useFetchConstant } from 'utils/hooks/general';

import { Form, Row, Col, message } from 'antd';

const StyledForm = styled(Form)`
  padding: 8% 4% !important;
  width: 100%;

  @media (min-width: 992px) {
    padding: 4% 8% !important;
    width: 68%;
  }
`;

const handleSubmit = (submissionData, onSubmitSuccess) => e => {
  const { form } = submissionData;
  e.preventDefault();
  form.validateFields((err, values) => {
    if (!err) {
      values = {
        enquirer: {
          firstName: values.name,
          lastName: values.lastName,
          contact: {
            countryCode: values.contactCountryCode,
            contactNumber: values.contactNumber
          },
          email: values.email,
          streetAddress: values.propertyAddress,
          identity: values.identity
        },
        source: values.source,
        types: values.enquiryType,
        remarks: values.remarks
      };
      submitEnquiry(values)
        .then(() => {
          onSubmitSuccess();
        })
        .catch(error => {
          message.error(error.message);
        });
    }
  });
};

const getLocationAddress = location => {
  if (location && location.state && location.state.address) {
    return location.state.address;
  }
  return '';
};

export const EnquiryForm = ({ location, ...props }) => {
  const { form } = props;
  const defaultAddress = getLocationAddress(location);
  const [successfullySubmitted, setSuccessfullySubmitted] = useState(false);
  const { selections: enquiryTypeSelections } = useFetchConstant('enquiryTypes');
  const { selections: enquirySourceSelections } = useFetchConstant('enquirySources');
  const { selections: enquirerIdentitySelections } = useFetchConstant('enquirerIdentities');
  const { selections: countryPhoneCodeSelections } = useFetchConstant('countries', 'Error while getting countries.', 'phoneCode', 'phoneCode');

  return !successfullySubmitted ? (
    <StyledForm onSubmit={handleSubmit({ form }, () => setSuccessfullySubmitted(true))}>
      <Row>
        <Heading size="regular">Get in touch!</Heading>
      </Row>
      <Row gutter={16}>
        <Col span={12}>
          <FormInput
            form={form}
            name="name"
            label="First Name"
            placeholder="John"
            size="large"
            autoComplete="new-user-name"
            requiredErrorMessage="Please enter your first name."
          />
        </Col>
        <Col span={12}>
          <FormInput
            form={form}
            name="lastName"
            label="Last Name"
            placeholder="Doe"
            size="large"
            requiredErrorMessage="Please enter your last name."
          />
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={12}>
          <FormInput
            form={form}
            name="email"
            label="Email"
            placeholder="email@example.com"
            size="large"
            autoComplete="new-user-email"
            requiredErrorMessage="Please enter your email."
            extraRules={[
              {
                pattern: '[a-z0-9._%+-]+@[a-z0-9.-]+.[a-z]{2,3}$',
                message: 'Please enter a valid email address.'
              }
            ]}
          />
        </Col>
        <Col span={12}>
          <FormPhoneNumber form={form} countryPhoneCodeSelections={countryPhoneCodeSelections} />
        </Col>
      </Row>
      <Row>
        <GoogleMapsSearch
          form={form}
          size="large"
          label="Address"
          defaultValue={defaultAddress}
          requiredErrorMessage="Please provide us an address to follow up."
        />
      </Row>
      <Row gutter={16}>
        <Col span={12}>
          <FormSelection
            form={form}
            name="source"
            label="Where did you hear about us?"
            placeholder="eg. Facebook"
            size="large"
            selections={enquirySourceSelections}
            requiredErrorMessage="Please tell us where you heard about us."
          />
        </Col>
        <Col span={12}>
          <FormSelection
            form={form}
            name="identity"
            label="Identity"
            placeholder="eg. Owner"
            size="large"
            selections={enquirerIdentitySelections}
            requiredErrorMessage="Please let us know how we can identify you."
          />
        </Col>
      </Row>
      <Row>
        <FormCheckbox
          form={form}
          name="enquiryType"
          label="Enquiry Type"
          size="large"
          selections={enquiryTypeSelections}
          requiredErrorMessage="Please select at least one enquiry type."
        />
      </Row>
      <Row>
        <FormInput
          form={form}
          name="remarks"
          label="Remarks"
          placeholder="Any additional remarks or message (Optional)"
          size="large"
          inputType="textarea"
          extraProps={{ rows: '3' }}
        />
      </Row>
      <Row type="flex" justify="space-between" gutter={16}>
        <Col span={6}>
          <Button size="large" type="secondary" toUrl="/">
            Home
          </Button>
        </Col>
        <Col span={6}>
          <Form.Item>
            <Button type="primary" size="large" htmlType="submit">
              Submit
            </Button>
          </Form.Item>
        </Col>
      </Row>
    </StyledForm>
  ) : (
    <EnquirySubmitted />
  );
};

EnquiryForm.propTypes = {
  location: PropTypes.object
};

EnquiryForm.defaultProps = {
  location: {}
};

export default withRouter(Form.create()(EnquiryForm));
